import { Link } from 'react-router-dom';
import { GraduationCap, ArrowUpRight } from 'lucide-react';

/**
 * RecruitmentToStudyAbroadStrip — compact cross-link block placed near the
 * footer of recruitment country pages (Canada/Australia/UK/Germany/USA hubs)
 * to surface the matching Study Abroad track for younger applicants who
 * may be a better fit for student visas than skilled-worker recruitment.
 *
 * SEO value:
 *   - Adds high-relevance internal link between two clusters (recruitment ↔
 *     study abroad) — Google rewards topical adjacency.
 *   - Helps deindex thin pathways and concentrate equity on the more
 *     content-rich Study Abroad guides we built in Week 1-2.
 *
 * Props:
 *   country     — display label, e.g. "Canada"
 *   studyHref   — e.g. "/study-abroad/canada/"
 *   visaHref    — optional inline secondary link (e.g. country-specific visa guide)
 *   visaLabel   — label for the secondary link
 */
const RecruitmentToStudyAbroadStrip = ({
  country,
  studyHref,
  visaHref,
  visaLabel,
}) => (
  <section
    className="py-12 bg-gradient-to-br from-slate-50 to-white border-t border-slate-200"
    aria-labelledby="rec-to-sa-heading"
    data-testid={`rec-to-sa-${country?.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="container mx-auto px-6">
      <div className="max-w-4xl mx-auto rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex flex-shrink-0 w-12 h-12 rounded-xl bg-zafco-blue/10 items-center justify-center">
            <GraduationCap className="w-6 h-6 text-zafco-blue" />
          </div>
          <div className="flex-1">
            <p className="text-[11px] font-semibold tracking-[0.18em] text-[#C5A028] uppercase mb-1.5">
              Considering Study Abroad?
            </p>
            <h2 id="rec-to-sa-heading" className="text-xl sm:text-2xl font-bold text-zafco-blue font-montserrat leading-tight mb-2">
              Many of our {country} placements started as students
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              If you&apos;re under 32 with a graduate degree, a {country} student visa often beats
              direct PR — better job-search runway, scholarships, and a smoother PR conversion.
              Explore our MEA-approved {country} education advisory.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to={studyHref}
                className="inline-flex items-center gap-1.5 bg-zafco-blue hover:bg-zafco-blue/90 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors"
                data-testid={`rec-to-sa-primary-${country?.toLowerCase().replace(/\s+/g, '-')}`}
              >
                Explore {country} Study Abroad
                <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
              {visaHref && (
                <Link
                  to={visaHref}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-zafco-blue hover:text-zafco-teal underline-offset-4 hover:underline px-2 py-2.5 transition-colors"
                  data-testid={`rec-to-sa-secondary-${country?.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {visaLabel} →
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default RecruitmentToStudyAbroadStrip;
